/**
 * @author        matthew@zumper.com (Matthew Talamini)
 * @copyright     Copyright (c) 2024, Zumper
 * @description   BuildingReviewsData Bizo
 */

import {
  type BuildingReviewsData as BuildingReviewsDataType,
  type BuildingReviewsResponse as BuildingReviewsResponseType,
  type BuildingReviewsReview as BuildingReviewsReviewType,
  BuildingReviewsSourceNames,
  type BuildingReviewsSource as BuildingReviewsSourceType,
  type BuildingReviewsSources as BuildingReviewsSourcesType,
  type RawBuildingReviewsData,
  type RawBuildingReviewsResponse,
  type RawBuildingReviewsReview,
  type RawBuildingReviewsSource,
  type RawBuildingReviewsSources,
} from './buildingReviews.types'

import { Bizo, autoclass } from './base'

const BUILDING_REVIEWS_RESPONSE_DEFINITION = {
  date: 'date',
  body: 'body',
} as const

export const BuildingReviewsResponse = autoclass<
  BuildingReviewsResponseType,
  RawBuildingReviewsResponse
>(
  'BuildingReviewsResponse',
  class BuildingReviewsResponse extends Bizo<
    BuildingReviewsResponseType,
    RawBuildingReviewsResponse
  > {},
  BUILDING_REVIEWS_RESPONSE_DEFINITION
)

const BUILDING_REVIEWS_REVIEW_DEFINITION = {
  name: 'name',
  avatarUrl: 'avatar_url',
  date: 'date',
  rating: 'rating',
  body: 'body',
  response: {
    name: 'response',
    type: BuildingReviewsResponse,
  },
} as const

export const BuildingReviewsReview = autoclass<BuildingReviewsReviewType, RawBuildingReviewsReview>(
  'BuildingReviewsReview',
  class BuildingReviewsReview extends Bizo<BuildingReviewsReviewType, RawBuildingReviewsReview> {},
  BUILDING_REVIEWS_REVIEW_DEFINITION
)

const BUILDING_REVIEWS_SOURCE_DEFINITION = {
  aggregateRating: 'aggregate_rating',
  ratingScale: 'rating_scale',
  reviewCount: 'review_count',
  externalUrl: 'external_url',
  lastPulled: 'last_pulled',
  reviews: {
    isArray: true,
    name: 'reviews',
    type: BuildingReviewsReview,
  },
} as const

export const BuildingReviewsSource = autoclass<BuildingReviewsSourceType, RawBuildingReviewsSource>(
  'BuildingReviewsSource',
  class BuildingReviewsSource extends Bizo<BuildingReviewsSourceType, RawBuildingReviewsSource> {},
  BUILDING_REVIEWS_SOURCE_DEFINITION
)

const BUILDING_REVIEWS_SOURCES_DEFINITION = {
  [BuildingReviewsSourceNames.GOOGLE]: {
    name: BuildingReviewsSourceNames.GOOGLE,
    type: BuildingReviewsSource,
  },
} as const

export const BuildingReviewsSources = autoclass<
  BuildingReviewsSourcesType,
  RawBuildingReviewsSources
>(
  'BuildingReviewsSources',
  class BuildingReviewsSources extends Bizo<
    BuildingReviewsSourcesType,
    RawBuildingReviewsSources
  > {},
  BUILDING_REVIEWS_SOURCES_DEFINITION
)

const BUILDING_REVIEWS_DATA_DEFINITION = {
  sources: {
    name: 'sources',
    type: BuildingReviewsSources,
  },
} as const

export const BuildingReviewsData: typeof BuildingReviewsDataType = autoclass<
  BuildingReviewsDataType,
  RawBuildingReviewsData
>(
  'BuildingReviewsData',
  class BuildingReviewsData extends Bizo<BuildingReviewsDataType, RawBuildingReviewsData> {},
  BUILDING_REVIEWS_DATA_DEFINITION
)

export type BuildingReviewsData = BuildingReviewsDataType
